import { Injectable } from '@angular/core';
import { AbstractApiV2Service } from '../../../api/services/abstract-api-v2.service';
import { CompaniesData } from '../../companies/interfaces/companies-data.interface';
import { CompanyDetailsInterface, Integration, IntegrationsCount } from '../../companies/interfaces/company-details.interface';
import { HttpClient } from '@angular/common/http';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';
import { OrganizationsService } from '../../organizations/services/organizations.service';
import { CompanyDataModel } from '../../companies/company-data-model.model';
import { IdentityService } from '../../auth/services/identity.service';
import { BehaviorSubject, firstValueFrom, share } from 'rxjs';
import { CompaniesService } from '../../companies/services/companies.service';
import { OrganizationData } from '../../organizations/interfaces/OrganizationData';

@Injectable({
    providedIn: 'root',
})
export class SecurityaddonsService extends AbstractApiV2Service<CompaniesData> {
    public breachWarrantyIntegration: Integration | undefined;
    public childrenIntegrations: IntegrationsCount;
    public breachWarrantySubject = new BehaviorSubject<boolean>(false);
    protected resourceName: string = 'companies';

    constructor(
        http: HttpClient,
        backendErrorTranslator: BackendErrorTranslatorService,
        private companiesService: CompaniesService,
        private identityService: IdentityService,
        private organizationService: OrganizationsService,
    ) {
        super(http, backendErrorTranslator);

        this.childrenIntegrations = {
            telemetry: 0,
            breach_warranty: 0,
        };

        this.identityService.companyLoaded$.subscribe(async (loaded) => {
            if (!loaded) {
                return;
            }

            const company = this.identityService.company;
            this.childrenIntegrations = await firstValueFrom(this.companiesService.getCompanyIntegrationCount(company.id));
            const selectedOrganization = this.organizationService.currentlySelectedOrganization();
            if (selectedOrganization == null || selectedOrganization.id == company.id) {
                this.checkBreachWarrantyIntegration(company);
            }
        });

        this.organizationService.selectedOrganization$().subscribe((organization) => {
            const companyDetails = organization ? (organization as OrganizationData) : this.identityService.company;
            this.checkBreachWarrantyIntegration(companyDetails);
        });
    }

    public hasChildrenWithBreach() {
        return this.childrenIntegrations.breach_warranty > 0;
    }

    private checkBreachWarrantyIntegration(organization: OrganizationData) {
        var breachWarranty = CompanyDataModel.findBreachWarranty(organization as CompanyDetailsInterface);
        var childOrganization: boolean = organization?.id !== this.identityService.company?.id;

        if (!breachWarranty && childOrganization) {
            breachWarranty = CompanyDataModel.noBreachWarranty;
        }

        // Skip if the integration is already set and acknowledged
        if (this.breachWarrantyIntegration?.id === breachWarranty?.id && this.breachWarrantyIntegration?.acknowledged) {
            return;
        }

        this.breachWarrantyIntegration = breachWarranty;
        this.breachWarrantySubject.next(this.showBreachWarrantyIntegration());
    }

    public showBreachWarrantyIntegration(): boolean {
        return (
            this.breachWarrantyIntegration?.warranty_type != null ||
            (this.hasChildrenWithBreach() && this.organizationService.currentlySelectedOrganization() == null)
        );
    }

    public acknowledgeBreachWarrantyInTC() {
        if (!this.breachWarrantyIntegration) {
            throw new Error('Breach warranty integration is not set.');
        }

        const request = this.http
            .post(`/v2/integrations/breach-warranty/${this.breachWarrantyIntegration.gravityzone_id}/acknowledge`, {})
            .pipe(share());

        request.subscribe((result) => {
            this.breachWarrantyIntegration = (result as any).data;
        });

        return request;
    }

    public openClaim() {
        if (!this.breachWarrantyIntegration) {
            throw new Error('Breach warranty integration is not set.');
        }
        return this.http.post(`/v2/integrations/breach-warranty/${this.breachWarrantyIntegration.gravityzone_id}/open-claim`, {});
    }
}
